import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { debounce } from '../utils/debounce';

export interface ComboboxOption {
  value: string;
  text: string;
}

@customElement('imos-combobox')
export class ImosCombobox extends LitElement {
  @property({ type: String }) label = '';
  @property({ type: Array }) options: ComboboxOption[] = [];
  @property({ type: Boolean }) multiple = true;
  @property({ type: Boolean }) searchable = true;
  @property({ type: String, attribute: 'search-label' }) searchLabel = 'Search';
  @property({ type: String, attribute: 'more-label' }) moreLabel = 'more';
  @property({ type: Number, attribute: 'max-visible-pills' }) maxVisiblePills = 2;
  /** Preselected values; changing this from the outside overrides the current selection. */
  @property({ type: Array }) values: string[] = [];
  /** When false, a single-select keeps its value and offers no clear button. */
  @property({ type: Boolean }) clearable = true;
  /** Utility classes for the trigger box, e.g. to narrow a short-option select. */
  @property({ type: String, attribute: 'trigger-class' }) triggerClass = 'min-w-[220px]';

  @state() private selectedValues: Set<string> = new Set();
  @state() private visibleOptions: ComboboxOption[] = [];
  @state() private open = false;
  @state() private search = '';
  @state() private activeIndex = -1;

  private boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this);
  private debouncedFilter = debounce(() => this.filterOptions(), 300);

  // Light DOM — Tailwind classes work natively
  protected createRenderRoot(): this {
    return this;
  }

  protected willUpdate(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('options')) {
      this.visibleOptions = [...this.options];
    }
    if (changedProperties.has('values')) {
      this.selectedValues = new Set(this.values);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.boundCloseOnOutsideClick);
  }

  get selectedOptions(): ComboboxOption[] {
    return this.options.filter((o) => this.selectedValues.has(o.value));
  }

  get selectedValuesArray(): string[] {
    return [...this.selectedValues];
  }

  private filterOptions(): void {
    this.activeIndex = -1;
    if (!this.search) {
      this.visibleOptions = [...this.options];
      return;
    }
    const term = this.search.toLowerCase();
    this.visibleOptions = this.options.filter((o) =>
      o.text.toLowerCase().includes(term),
    );
  }

  private async positionDropdown(): Promise<void> {
    const reference = this.querySelector<HTMLElement>('[data-combobox-trigger]');
    const floating = this.querySelector<HTMLElement>('[data-combobox-dropdown]');
    if (!reference || !floating) return;

    const { x, y } = await computePosition(reference, floating, {
      placement: 'bottom-start',
      middleware: [
        offset(5),
        flip({ fallbackPlacements: ['top-start'] }),
        shift({ boundary: document.getElementById('main') ?? undefined }),
      ],
    });

    Object.assign(floating.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  }

  private openDropdown(): void {
    this.open = true;
    this.search = '';
    this.activeIndex = -1;
    this.visibleOptions = [...this.options];
    requestAnimationFrame(() => {
      this.positionDropdown();
      this.querySelector<HTMLInputElement>('[data-combobox-search]')?.focus();
      document.addEventListener('click', this.boundCloseOnOutsideClick);
    });
  }

  private closeDropdown(): void {
    this.open = false;
    this.search = '';
    document.removeEventListener('click', this.boundCloseOnOutsideClick);
  }

  private closeOnOutsideClick(e: Event): void {
    if (!this.contains(e.target as Node)) {
      this.closeDropdown();
    }
  }

  private toggleOption(option: ComboboxOption): void {
    let next: Set<string>;
    if (this.multiple) {
      next = new Set(this.selectedValues);
      if (next.has(option.value)) {
        next.delete(option.value);
      } else {
        next.add(option.value);
      }
    } else {
      this.closeDropdown();
      if (this.selectedValues.has(option.value)) {
        if (!this.clearable) return;
        next = new Set();
      } else {
        next = new Set([option.value]);
      }
    }
    this.selectedValues = next;
    this.dispatchEvent(
      new CustomEvent('imos-combobox-change', {
        bubbles: true,
        detail: { values: [...next] },
      }),
    );
  }

  private removeOption(option: ComboboxOption): void {
    if (!this.multiple && !this.clearable) return;
    const next = new Set(this.selectedValues);
    next.delete(option.value);
    this.selectedValues = next;
    this.dispatchEvent(
      new CustomEvent('imos-combobox-change', {
        bubbles: true,
        detail: { values: [...next] },
      }),
    );
  }

  resetSelection(): void {
    if (!this.multiple && !this.clearable) return;
    this.selectedValues = new Set();
    this.dispatchEvent(
      new CustomEvent('imos-combobox-change', {
        bubbles: true,
        detail: { values: [] },
      }),
    );
  }

  private onSearchInput(e: Event): void {
    this.search = (e.target as HTMLInputElement).value;
    this.debouncedFilter();
  }

  private onSearchKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.visibleOptions.length - 1);
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.scrollActiveIntoView();
        break;
      case 'Enter':
        e.preventDefault();
        if (this.activeIndex >= 0 && this.activeIndex < this.visibleOptions.length) {
          this.toggleOption(this.visibleOptions[this.activeIndex]);
        }
        break;
      case 'Escape':
        if (this.search) {
          this.search = '';
          this.activeIndex = -1;
          this.filterOptions();
        } else {
          this.closeDropdown();
        }
        break;
      case 'Delete':
      case 'Backspace':
        if (!this.search && this.selectedValues.size > 0) {
          const last = [...this.selectedValues].pop()!;
          const option = this.options.find((o) => o.value === last);
          if (option) this.removeOption(option);
        }
        break;
    }
  }

  private scrollActiveIntoView(): void {
    requestAnimationFrame(() => {
      const item = this.querySelector<HTMLElement>(`[data-option-index="${this.activeIndex}"]`);
      item?.scrollIntoView({ block: 'nearest' });
    });
  }

  protected render() {
    const selected = this.selectedOptions;
    const pillsToShow = selected.slice(0, this.maxVisiblePills);
    const moreCount = selected.length - this.maxVisiblePills;

    return html`
      <div class="relative">
        <div data-combobox-trigger class="flex">
          <div
            class="${this.triggerClass} py-1.5 flex items-center bg-white dark:bg-gray-300 pr-2xs rounded-full transition-shadow focus-within:ring-2 focus-within:ring-brand ${selected.length > 0 && this.multiple ? 'pl-2' : 'pl-2xs'}"
          >
            <div class="flex flex-auto flex-wrap items-center min-w-0" @click=${() => this.openDropdown()}>
              ${selected.length > 0
                ? this.multiple
                  ? html`
                      <div class="py-px flex gap-x-1 mr-2">
                        ${repeat(
                          pillsToShow,
                          (o) => o.value,
                          (option) => html`
                            <div
                              class="flex justify-center items-center py-1 px-2 rounded-full bg-gray-200 dark:bg-gray-400 text-2xs gap-x-2 mr-1 leading-none"
                            >
                              <div
                                class="max-w-[75px] text-ellipsis overflow-hidden whitespace-nowrap"
                                title=${option.text}
                              >
                                ${option.text}
                              </div>
                              <div
                                class="cursor-pointer"
                                @click=${(e: Event) => {
                                  e.stopPropagation();
                                  this.removeOption(option);
                                }}
                              >
                                <i class="icon--imos-webicon_x text-secondary-alt"></i>
                              </div>
                            </div>
                          `,
                        )}
                        ${moreCount > 0
                          ? html`
                              <div
                                class="py-1 px-2 rounded-full bg-secondary text-2xs leading-none cursor-pointer"
                                @click=${() => this.openDropdown()}
                              >
                                ${moreCount} ${this.moreLabel}
                              </div>
                            `
                          : nothing}
                      </div>
                    `
                  : html`
                      <input
                        .value=${selected[0].text}
                        size=${Math.max(selected[0].text.length, 1)}
                        class="p-0 text-xs bg-inherit cursor-pointer border-0 focus:outline-0 focus:ring-0 flex-1 min-w-0 text-ellipsis"
                        readonly
                      />
                      ${this.clearable
                        ? html`<button
                            class="cursor-pointer flex items-center mr-2"
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              this.removeOption(selected[0]);
                            }}
                          >
                            <i class="icon--imos-webicon_x text-secondary-alt text-2xs"></i>
                          </button>`
                        : nothing}
                    `
                : html`
                    <input
                      placeholder=${this.label}
                      size=${Math.max(this.label.length, 1)}
                      class="p-0 text-xs bg-inherit cursor-pointer border-0 focus:outline-0 focus:ring-0 w-full min-w-0 text-ellipsis"
                      readonly
                    />
                  `}
            </div>

            <div
              class="text-gray-300 h-full pl-2 border-l flex items-center border-secondary-alt"
            >
              <button
                type="button"
                class="flex items-center cursor-pointer leading-none text-xs outline-none focus:outline-none"
                @click=${() => (this.open ? this.closeDropdown() : this.openDropdown())}
              >
                <i
                  class="icon--imos-webicon_${this.open ? 'up' : 'down'} text-secondary-alt"
                ></i>
              </button>
            </div>
          </div>
        </div>

        <div
          data-combobox-dropdown
          class="z-50 min-w-full absolute"
          style="display: ${this.open ? '' : 'none'}"
        >
          <div
            class="bg-white dark:bg-gray-300 shadow-lg rounded text-xs font-light overflow-hidden"
          >
            <div class="flex flex-col w-full">
              ${this.searchable
                ? html`
                    <div class="flex items-center gap-x-2 px-2xs border-b border-secondary">
                      <i class="icon--imos-webicon_search"></i>
                      <input
                        data-combobox-search
                        type="text"
                        autocomplete="off"
                        class="w-10 grow leading-loose bg-inherit border-0 focus:outline-0 focus:ring-0"
                        .placeholder=${this.searchLabel}
                        .value=${this.search}
                        @input=${this.onSearchInput}
                        @keydown=${this.onSearchKeydown}
                      />
                      <div class="flex gap-x-2">
                        <kbd
                          class="border border-secondary rounded px-1 text-2xs text-secondary-alt uppercase cursor-pointer"
                          @click=${() => {
                            this.search = '';
                            this.filterOptions();
                          }}
                        >
                          Esc
                        </kbd>
                      </div>
                    </div>
                  `
                : nothing}

              <ul
                class="z-10 m-0 w-full shadow-lg max-h-80 overflow-y-auto list-none p-0"
                tabindex="-1"
                role="listbox"
              >
                ${repeat(
                  this.visibleOptions,
                  (o) => o.value,
                  (option, index) => {
                    const isSelected = this.selectedValues.has(option.value);
                    const isActive = index === this.activeIndex;
                    return html`
                      <li
                        data-option-index=${index}
                        class=${classMap({
                          'text-main cursor-default select-none relative mb-0 py-1 px-2xs cursor-pointer w-full border-secondary border-b transition-color hover:bg-secondary flex gap-x-2xs':
                            true,
                          'bg-secondary-alt': isSelected,
                          'bg-secondary': isActive && !isSelected,
                        })}
                        role="option"
                        aria-selected=${isSelected}
                      >
                        <button
                          class="group font-light flex gap-2xs w-full items-center justify-between"
                          @click=${() => this.toggleOption(option)}
                        >
                          <span class="text-left">${option.text}</span>
                          ${isSelected
                            ? html`<span class="leading-none"
                                ><i class="icon--imos-webicon_checkmark"></i
                              ></span>`
                            : nothing}
                        </button>
                      </li>
                    `;
                  },
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
